<template>
  <div v-if="!!uploadUrl">
    <v-container>
      <div class="container-title mb-6">Adicione imagens e exames do paciente</div>
      <v-card outlined rounded="lg" class="mb-8">
        <v-card-title> Compartilhar link com paciente para envio de imagens </v-card-title>
        <v-container>
          <v-row justify="start">
            <v-col cols="2" class="text-center">
              <v-btn x-large icon target="_blank" color="primary" @click="copyToClipboard">
                <v-icon x-large>mdi-link</v-icon>
              </v-btn>
              <p class="mb-0">Copiar link</p>
            </v-col>
            <v-col cols="2" class="text-center">
              <v-btn x-large icon :disabled="!patientMobile" :href="whatsappLink" target="_blank" color="primary">
                <v-icon x-large>mdi-whatsapp</v-icon>
              </v-btn>
              <p class="mb-0">Compartilhar<br />por WhatsApp<br />{{ patientMobile }}</p>
            </v-col>
            <v-col cols="2" class="text-center">
              <v-btn
                x-large
                icon
                :disabled="!patientMobile || smsCooldown"
                target="_blank"
                color="primary"
                @click="sendSms"
              >
                <v-icon x-large>mdi-message-processing</v-icon>
              </v-btn>
              <p class="mb-0">Enviar por SMS<br />{{ patientMobile }}</p>
              <small v-if="smsCooldown">Aguarde {{ resendSmsSeconds }}s para poder enviar novamente</small>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card outlined rounded="lg" class="mb-8">
        <v-card-title> Subir fotos do celular com QR code </v-card-title>
        <v-container>
          <v-row>
            <v-col>
              <qrcode-vue :value="uploadUrl" level="H" :size="196" />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card outlined rounded="lg" class="mb-8">
        <v-card-title> Subir arquivos do computador </v-card-title>
        <v-container>
          <v-row>
            <v-col cols="12">
              <attendance-file-uploader
                :flat="true"
                :style="{ width: $vuetify.breakpoint.smAndDown ? '100%' : 'auto' }"
                class="mt-2"
                :attendanceId="attendanceId"
                :uuid="uuid"
                accept=".dcm,.dicom,application/dicom,image/*,application/zip,application/msword,application/pdf"
                :errors.sync="fileErrors"
                :uploaded.sync="uploaded"
                hide-errors
                hide-uploaded
                @send:finished="
                  $event.uploaded === $event.total
                    ? $root.ok('Arquivos enviados com sucesso!')
                    : $root.error('Um ou mais arquivos não puderam ser enviados!')
                "
              />
            </v-col>
            <v-col>
              <div v-if="fileErrors.length > 0">
                <strong class="red--text">Atenção</strong>
                <p class="mb-2">Um erro impediu o envio dos arquivos abaixo. Tente fazer o upload novamente.</p>
                <p class="mb-0">{{ fileErrors.join(', ') }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
      <v-card flat rounded="lg">
        <div :style="{ 'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : 0 }">
          <div v-if="loadingFiles" class="d-flex flex-column fill-height gap-2 justify-center align-center">
            <v-progress-circular indeterminate />
            <p>Carregando imagens do atendimento...</p>
          </div>
          <div v-else>
            <div class="text-body-1">{{ `${uploaded.length} arquivo(s) enviados` }}</div>
            <v-sheet class="d-flex gap-4 flex-wrap overflow-y-auto mt-2">
              <div class="p-relative" v-for="document in uploaded" :key="`document-${document.id}`">
                <v-chip outlined @click="download(document)" @click:close="removeFile(document)" close>
                  {{ document.name }}
                </v-chip>
              </div>
            </v-sheet>
          </div>
        </div>
      </v-card>
    </v-container>
    <ReferenceDocumentDialog @document-deleted="documentDeleted" ref="documentDialog"></ReferenceDocumentDialog>
  </div>
  <v-sheet v-else>
    <h1>
      Este atendimento foi gerado sem a possibilidade de enviar imagens.
      <br />
      Se você acredita que isto é um erro, entre em contato com o Suporte.
    </h1>
  </v-sheet>
</template>

<script>
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import QrcodeVue from 'qrcode.vue'
import { createNamespacedHelpers } from 'vuex'
import { DOCUMENT_PHOTO } from '@/constants/attendance'
import AttendanceFileUploader from '@/components/AttendanceForm/AttendanceFileUploader.vue'
import ReferenceDocumentDialog from '@/components/AttendanceForm/ReferenceDocumentDialog.vue'

const { mapState: attendanceStateMap, mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

export default {
  components: {
    AttendanceFileUploader,
    QrcodeVue,
    ReferenceDocumentDialog,
  },
  extends: BasePhase,
  computed: {
    ...attendanceStateMap(['attendance']),
    ...attendanceGettersMap(['patientMobile', 'patientEmail']),
    uuid() {
      return this.attendance.patient.uuid
    },
    patientId() {
      return this.attendance.patient.id
    },
    uploadUuid() {
      return this.attendance?.uploadPhotoUuid
    },
    attendanceId() {
      return parseInt(this.attendance.id)
    },
    uploadUrl() {
      if (!this.uploadUuid) {
        return null
      }
      return `${window.location.origin}/upload-attendance-photo?u=${this.uuid}&a=${this.attendanceId}`
    },
    whatsappLink() {
      if (!this.uploadUrl || !this.patientMobile) {
        return null
      }
      const mobileNumber = this.$util.toNumbersOnly(this.patientMobile)
      const message = this.$util.urlEncode(`Envie fotos ao seu médico em ${this.uploadUrl}`)
      return `https://api.whatsapp.com/send?phone=55${mobileNumber}&text=${message}&lang=pt_br`
    },
  },
  data() {
    return {
      fileErrors: [],
      uploaded: [],
      loadingFiles: false,
      smsCooldown: false,
      resendSmsSeconds: 60,
      pollInterval: null,
    }
  },
  methods: {
    getDefaultData() {
      return {}
    },
    copyToClipboard() {
      this.$util.copyToClipboard(this.uploadUrl)
      this.$root.info('Link copiado para a área de transferência')
    },
    showImage(document) {
      this.$refs.documentDialog.openDialog(document)
    },
    isPhoto(document) {
      return document.type === DOCUMENT_PHOTO
    },
    async loadAttendanceFiles(lastCheck = null) {
      if (!this.attendanceId) {
        return
      }
      try {
        const result = await this.$api.getAttendanceDocumentFiles(this.attendance.id, {
          sortBy: ['id'],
          sortDesc: [true],
          lastCheck,
        })
        if (this.haveFilesChanged(this.uploaded, result.items)) {
          this.uploaded = result.items
        }
      } catch (error) {
        this.$root.error(error)
      }
    },
    async pollForUpdates() {
      let lastCheck = new Date().toISOString()
      this.pollInterval = setInterval(async () => {
        try {
          const result = await this.$api.getAttendanceDocumentFiles(this.attendance.id, {
            sortBy: ['id'],
            sortDesc: [true],
            lastCheck,
          })
          if (this.haveFilesChanged(this.uploaded, result.items)) {
            this.uploaded = result.items
            lastCheck = new Date().toISOString()
          }
        } catch (error) {
          this.$root.error(error)
        }
      }, 3000)
    },
    haveFilesChanged(oldFiles, newFiles) {
      if (oldFiles.length !== newFiles.length) {
        return true
      }
      const oldFileIds = oldFiles.map((file) => file.id).sort()
      const newFileIds = newFiles.map((file) => file.id).sort()
      for (let i = 0; i < oldFileIds.length; i++) {
        if (oldFileIds[i] !== newFileIds[i]) {
          return true
        }
      }
      return false
    },
    documentDeleted() {
      this.loadAttendanceFiles()
    },
    async removeFile(document) {
      if (!(await this.$root.confirm('Tem certeza de que deseja remover este arquivo?'))) {
        return
      }
      try {
        await this.$api.removePatientAttendanceFile(document.id)
        this.loadAttendanceFiles()
        this.$root.ok('Arquivo removido com sucesso!')
      } catch (error) {
        this.$root.error(error)
      }
    },
    async sendSms() {
      try {
        this.smsCooldown = true
        await this.$api.sendSmsUploadPhoto(this.attendanceId)
        setTimeout(() => (this.smsCooldown = false), this.resendSmsSeconds * 1000)
      } catch (error) {
        this.smsCooldown = false
        this.$root.error(error)
      }
    },
    async download(doc) {
      try {
        const a = document.createElement('a')
        a.href = doc.url
        a.download = doc.name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (error) {
        this.$root.error('Não foi possível realizar o download')
      }
    },
  },
  async mounted() {
    await this.pollForUpdates()
  },
  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
  },
}
</script>

<style scoped>
.thumb {
  border: 1px dotted lightgray;
}
.container-title {
  font-size: 1.375rem;
  font-weight: 400;
  line-height: 28px;
  text-align: left;
  color: #383838;
}
</style>
